/**
 * How a module-wise syllabus becomes classes — the syllabus builder's preview.
 *
 * This is the twin of vc_be/utils/classPlanner.js (`planClasses`), which forms a
 * batch's classes when it starts. The editor shows what the server will make,
 * so the two must agree: their tests pin the same cases. Change both.
 *
 *   • Topics are taught in order; every module starts a new class.
 *   • A class takes topics while their minutes fit the class length, with a
 *     15% tolerance.
 *   • A topic longer than a class becomes parts; the last part's class may take
 *     the next topic if there is room.
 *   • `startsNewClass` pins a boundary before a topic; `classTitle` names the
 *     class that starts there.
 *   • A topic with no duration counts as 60 minutes.
 */

export const DEFAULT_CLASS_MINUTES = 60;
export const MIN_CLASS_MINUTES = 15;
export const MAX_CLASS_MINUTES = 600;
export const DEFAULT_TOPIC_MINUTES = 60;
const FIT_TOLERANCE = 0.15;

export const clampClassMinutes = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(MAX_CLASS_MINUTES, Math.max(MIN_CLASS_MINUTES, n));
};

const topicMinutes = (t) => {
  const n = Math.round(Number(t?.minutes ?? t?.estimatedDuration));
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_TOPIC_MINUTES;
};

const splitEvenly = (list, parts) => {
  const items = Array.isArray(list) ? list : [];
  const size = Math.ceil(items.length / parts) || 0;
  return Array.from({ length: parts }, (_, i) => items.slice(i * size, (i + 1) * size));
};

/** "Loops", "Loops and Arrays", "Loops and 2 more". */
export const classTitleFor = (topics) => {
  if (!topics.length) return "";
  const first = topics[0];
  const firstName = first.parts > 1 ? `${first.name} (part ${first.part} of ${first.parts})` : first.name;
  if (topics.length === 1) return firstName;
  if (topics.length === 2) return `${first.name} and ${topics[1].name}`;
  return `${first.name} and ${topics.length - 1} more`;
};

/**
 * @param {object} args
 * @param {{ uid?, key?, title, topics: { uid?, key?, title, minutes, subtopics?, startsNewClass?, classTitle? }[] }[]} args.modules
 *        — in teaching order (the editor's order IS the module order)
 * @param {number} args.classMinutes
 * @returns {{ seq, moduleIndex, moduleTitle, title, minutes, startTopicUid, titleOverridden, topics }[]}
 */
export function planClasses({ modules, classMinutes }) {
  const length = clampClassMinutes(classMinutes) || DEFAULT_CLASS_MINUTES;
  const limit = length * (1 + FIT_TOLERANCE);
  const classes = [];
  let cur = null;

  const close = () => {
    if (cur && cur.topics.length) classes.push(cur);
    cur = null;
  };
  const open = (moduleIndex, moduleTitle, startTopicUid, titleOverride) => ({
    moduleIndex,
    moduleTitle,
    startTopicUid,
    titleOverride: titleOverride || "",
    minutes: 0,
    topics: [],
  });

  (modules || []).forEach((m, moduleIndex) => {
    close();
    (m.topics || []).forEach((t) => {
      const name = String(t.title || "").trim();
      if (!name) return;
      const minutes = topicMinutes(t);
      const override = String(t.classTitle || "").trim();
      if (t.startsNewClass) close();

      if (minutes > limit) {
        const parts = Math.ceil(minutes / length);
        const subs = splitEvenly(t.subtopics, parts);
        close();
        for (let p = 1; p <= parts; p += 1) {
          const share = p < parts ? length : minutes - length * (parts - 1);
          cur = open(moduleIndex, m.title, t.uid, p === 1 ? override : "");
          cur.topics.push({ uid: t.uid, key: t.key, name, minutes: share, part: p, parts, subtopics: subs[p - 1] });
          cur.minutes = share;
          if (p < parts) close();
        }
        return;
      }

      if (!cur) cur = open(moduleIndex, m.title, t.uid, override);
      else if (cur.minutes + minutes > limit) {
        close();
        cur = open(moduleIndex, m.title, t.uid, override);
      }
      cur.topics.push({ uid: t.uid, key: t.key, name, minutes, subtopics: t.subtopics || [] });
      cur.minutes += minutes;
    });
  });
  close();

  return classes.map((c, i) => ({
    seq: i + 1,
    moduleIndex: c.moduleIndex,
    moduleTitle: c.moduleTitle,
    title: c.titleOverride || classTitleFor(c.topics),
    titleOverridden: !!c.titleOverride,
    minutes: c.minutes,
    startTopicUid: c.startTopicUid,
    topics: c.topics,
  }));
}
